import React from 'react'
import cn from 'classnames'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { FixedHorizontalSpacer, Typography } from '@grammarly/badger-ui'
import { FormattedMessage } from 'react-intl'

import { ContentProps } from '../../renderer'
import { TypeComponentTextInput } from '../../../lib/types/component-text-input'
import { useUserService } from '../../../lib/hooks'
import { InputError, useError } from '../../inputs/hooks'
import { TextLimitConfig, charactersToWords, getMaxCharacterLimit } from '../../inputs/validations'
import { CharacterCount } from '../../inputs/character-count'
import { WordCount } from '../../inputs/word-count'
import { calculateWordCount } from '../../../lib/utils/strings'

import styles from './ai-detector-shared.module.scss'

const { C4 } = Typography

interface MinimumCharacterLimitMessageProps {
  textLimitConfig: TextLimitConfig
  name: string
}

const getRemainingLength = (textLimitConfig: TextLimitConfig, value: string) => {
  if (textLimitConfig.minCharacterLimit) {
    const limit = textLimitConfig.useWordLimit
      ? textLimitConfig.minCharacterLimit / charactersToWords
      : textLimitConfig.minCharacterLimit
    const length = textLimitConfig.useWordLimit ? calculateWordCount(value) : value.length
    const remainingLength = limit - length

    return remainingLength <= 0 ? null : remainingLength
  }

  return null
}

const MinimumCharacterLimitMessage: React.FC<MinimumCharacterLimitMessageProps> = ({ textLimitConfig, name }) => {
  const { errors } = useFormState({ name })
  const value = useWatch({ name })

  const errorMessage: string | React.ReactNode | undefined = errors[name]?.message?.toString()

  const remainingLength = getRemainingLength(textLimitConfig, value)

  if (remainingLength === null) {
    return null
  }

  const lengthType = textLimitConfig.useWordLimit ? 'word' : 'character'
  const hasError = errorMessage === InputError.BelowCharacterLimit

  return (
    <>
      <span className={cn(styles.minCharacterLimit, hasError && styles.error)}>
        <FormattedMessage
          id="aiDetector.errorOrCounter.minimumCharacterLimitMessage"
          description="AI Detector - Error Or Counter - Minimum Character Limit Message"
          defaultMessage="Enter <strong>{remainingLength}</strong> more {lengthType, select, word {word} other {character}}{plural, select, true {s} other {}}"
          values={{
            remainingLength,
            lengthType,
            plural: remainingLength > 1 ? 'true' : 'false',
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </span>
      {!hasError && <FixedHorizontalSpacer multiplier={1} />}
    </>
  )
}

export const AIDetectorErrorOrCounter: React.FC<ContentProps<TypeComponentTextInput>> = ({
  structure: {
    fields: { name, maxLength, minCharacterLimit, anonymousCharacterLimit, useWordLimit, hasLongformText },
  },
}) => {
  const { isAnonymous } = useUserService().currentUser
  const {
    formState: { isDirty, isSubmitted },
    watch,
  } = useFormContext()
  const value = watch(name)

  const textLimitConfig: TextLimitConfig = {
    maxCharacterLimit: getMaxCharacterLimit(maxLength, hasLongformText),
    minCharacterLimit,
    showErrorText: false,
    anonymousCharacterLimit,
    useWordLimit,
  }

  const error = useError(name, textLimitConfig)
  const hasErrors = !!error && (isDirty || isSubmitted)
  if (!hasErrors) {
    if (!value) {
      return null
    }

    const LengthCounter = useWordLimit ? WordCount : CharacterCount
    const maxChars =
      textLimitConfig.anonymousCharacterLimit && isAnonymous
        ? textLimitConfig.anonymousCharacterLimit
        : textLimitConfig.maxCharacterLimit

    const remainingLength = getRemainingLength(textLimitConfig, value)

    return (
      <C4 as="div" className={styles.counter}>
        <MinimumCharacterLimitMessage textLimitConfig={textLimitConfig} name={name} />
        {!remainingLength && <LengthCounter value={value} maxChars={maxChars} />}
      </C4>
    )
  }

  if (typeof error === 'string') {
    return (
      <C4 as="div" className={cn(styles.counter, styles.error)}>
        {error}
      </C4>
    )
  }

  return (
    <C4 as="div" className={cn(styles.counter, styles.counterError)}>
      <MinimumCharacterLimitMessage textLimitConfig={textLimitConfig} name={name} />
      {error}
    </C4>
  )
}
